import React, { useEffect, useRef, useState } from "react";
import { HomeHeader } from "../../../components/headers/HomeHeader"; // <-- adjust if needed

const HOME_HEADER_HEIGHT = 79; // adjust to match your HomeHeader height

export const EarlySeveritySignalDetection = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const tagRef = useRef(null);
  const dropdownRef = useRef(null);
  const year = new Date().getFullYear();

  // Close dropdown when clicking outside
  useEffect(() => {
    const onClickAway = (e) => {
      if (!menuOpen) return;
      const t = e.target;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(t) &&
        tagRef.current &&
        !tagRef.current.contains(t)
      ) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("click", onClickAway);
    return () => window.removeEventListener("click", onClickAway);
  }, [menuOpen]);

  return (
    <div style={{ width: "100%" }}>
      <HomeHeader headerColor="#0D131E" textColor="#ffffff" />

      {/* Scoped CSS (prefixed with .esd- to avoid collisions) */}
      <style>{`
        li {
          padding-top:10px;
          padding-bottom:0px;
        }

        .esd-root {
          --bg: #0a0c10;
          --panel: #0f131a;
          --panel-2: #121722;
          --text: #e6e9ef;
          --muted: #a7b0c0;
          --brand: #0b5cff;
          --brand-2: #8ab4ff;
          --ok: #1fbf75;
          --warn: #ffd166;
          --danger: #ff5d5d;
          --card-radius: 18px;
          --shadow: 0 10px 30px rgba(0,0,0,.35);
          --maxw: 1100px;
          --offset: ${HOME_HEADER_HEIGHT}px;
          color: var(--text);
          background:
            radial-gradient(1200px 800px at 10% -10%, rgba(11,92,255,.15), transparent 60%),
            radial-gradient(1200px 800px at 110% 10%, rgba(138,180,255,.1), transparent 60%),
            var(--bg);
          line-height: 1.55;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
          letter-spacing: normal; /* reset any global tracking */
          text-align: left; /* avoid parent center alignment */
        }

        .esd-container { max-width: var(--maxw); margin: 0 auto; padding: 24px; }

        /* Header under HomeHeader */
        .esd-header {
          position: sticky;
          top: var(--offset);
          z-index: 50;
          backdrop-filter: saturate(140%) blur(10px);
          background: linear-gradient(180deg, rgba(10,12,16,.9), rgba(10,12,16,.6));
          border-bottom: 1px solid rgba(255,255,255,.06);
        }
        .esd-nav { display:flex; align-items:center; justify-content:space-between; gap:16px; }
        .esd-logo { display:flex; align-items:center; gap:12px; text-decoration:none; color:var(--text); }
        .esd-mark { width:28px; height:28px; display:grid; place-items:center; border-radius:8px; background:linear-gradient(135deg, var(--brand), #2b90ff); box-shadow: var(--shadow); }
        .esd-word { font-weight:700; letter-spacing:.2px; }

        .esd-cta { display:flex; gap:10px; }
        .esd-btn {
          --padx: 14px; --pady: 10px;
          padding: var(--pady) var(--padx);
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,.12);
          color: var(--text);
          text-decoration: none;
          font-weight: 600; font-size: 14px;
          transition: transform .06s ease, background .2s ease, border-color .2s ease;
        }
        .esd-btn:hover { transform: translateY(-1px); }
        .esd-btn.primary { background: linear-gradient(135deg, var(--brand), #2b90ff); border-color: transparent; }
        .esd-btn.ghost { background: rgba(255,255,255,.03); }

        .esd-hero { padding: 56px 0 26px; position: relative; }
        .esd-hero h1 {
          font-size: clamp(34px, 3.8vw, 48px);
          line-height: 1.15;
          margin: 0 0 16px;
          letter-spacing: .3px;
          word-spacing: .5px;
        }
        .esd-hero p { color: var(--muted); font-size: clamp(16px, 1.7vw, 18px); max-width: 880px; }
        .esd-tag {
          display:inline-flex; align-items:center; gap:8px; padding:6px 12px;
          border-radius:999px; background: rgba(11,92,255,.12); color:#cfe0ff;
          border:1px solid rgba(138,180,255,.25); font-weight:600; font-size:13px; cursor:pointer;
        }

        .esd-grid { display:grid; gap:18px; }
        .esd-cols-3 { grid-template-columns: repeat(3, minmax(0,1fr)); }
        .esd-cols-2 { grid-template-columns: repeat(2, minmax(0,1fr)); }
        @media (max-width: 900px) { .esd-cols-3, .esd-cols-2 { grid-template-columns: 1fr; } }

        .esd-card { background: linear-gradient(180deg, var(--panel), var(--panel-2)); border:1px solid rgba(255,255,255,.06); border-radius: var(--card-radius); box-shadow: var(--shadow); padding: 20px 18px; }
        .esd-card h3 { margin: 0 0 8px; font-size: 18px; }
        .esd-card p { margin: 0; color: var(--muted); }

        .esd-kv { display:flex; gap:12px; align-items:flex-start; padding: 14px 0; border-top: 1px dashed rgba(255,255,255,.08); }
        .esd-kv:first-child { border-top: 0; }
        .esd-k { flex: 0 0 140px; color:#cfe0ff; font-weight:700; text-transform:uppercase; letter-spacing:.6px; font-size:12px; }
        .esd-v { flex: 1; color: var(--text); }

        .esd-badges { display:flex; gap:8px; flex-wrap:wrap; }
        .esd-badge { display:inline-flex; align-items:center; gap:8px; padding:8px 12px; border-radius:12px; background: rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.08); font-weight:600; color: var(--text); }

        .esd-section { margin: 30px 0; }
        .esd-section h2 { font-size:22px; margin:0 0 8px; }
        .esd-muted { color: var(--muted); }

        .esd-headline { display:flex; align-items:center; gap:12px; margin:28px 0 10px; }
        .esd-rule { height:1px; background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.2), rgba(255,255,255,0)); flex:1; }

        .esd-callout { border-left:3px solid var(--brand); background: rgba(11,92,255,.08); padding:14px 16px; border-radius:10px; }

        .esd-metrics { display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:12px; margin-top:12px; }
        .esd-metric { text-align:center; padding:16px; border-radius:14px; background: rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.08); }
        .esd-metric h4 { margin:0 0 4px; font-size:20px; }
        .esd-metric p { margin:0; color: var(--muted); font-size:14px; }
        @media (max-width: 900px) { .esd-metrics { grid-template-columns: 1fr; } }

        .esd-footer { margin: 40px 0 20px; color: var(--muted); font-size: 13px; text-align:center; }

        /* Dropdown menu */
        .esd-menu {
          position:absolute;
          background-color:#0f131a;
          min-width:260px;
          z-index:10;
          border-radius:10px;
          box-shadow: var(--shadow);
          border:1px solid rgba(255,255,255,.12);
          margin-top:8px;
        }
        .esd-menu a { color: var(--text); padding:12px; text-decoration:none; display:block; }
        .esd-menu a:hover { background-color: var(--brand-2); }

        @media print {
          .esd-header { display:none; }
          .esd-root { background:#fff; color:#111; }
          .esd-card { background:#fff; border-color:#ddd; box-shadow:none; }
          .esd-hero .esd-tag { background:#e9f1ff; color:#0b5cff; border-color:#c9ddff; }
        }
      `}</style>

      <div className="esd-root">
        {/* Page header (below HomeHeader) */}
        <header className="esd-header">
          <div className="esd-container esd-nav">
            <a
              className="esd-logo"
              href="#top"
              aria-label="Charlee.ai — Early Severity Signal Detection"
            >
              <span className="esd-mark" aria-hidden="true">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M12 2l2.2 5.7L20 10l-5.8 2.3L12 18l-2.2-5.7L4 10l5.8-2.3L12 2z"
                    fill="white"
                    opacity=".9"
                  />
                </svg>
              </span>
              <span className="esd-word">Charlee.ai</span>
            </a>
            <div className="esd-cta">
              <a className="esd-btn ghost" href="#value">
                Value
              </a>
              <a className="esd-btn ghost" href="#financial-impact">
                Financial Impact
              </a>
              <a className="esd-btn primary" href="#product">
                Product
              </a>
            </div>
          </div>
        </header>

        <main className="esd-container" style={{ paddingTop: "70px" }}>
          <section className="esd-hero">
            {/* Dropdown trigger */}
            <span
              ref={tagRef}
              className="esd-tag"
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={menuOpen}
              role="button"
            >
              Use Case 2 · Early Severity Signal Detection{" "}
              <span aria-hidden="true">▼</span>
            </span>

            {/* Dropdown (inline display to avoid global overrides) */}
            <div
              ref={dropdownRef}
              className="esd-menu"
              style={{ display: menuOpen ? "block" : "none" }}
            >
              {/* Keep these as normal anchors so crawlers follow them; point them at your app routes */}
              <a href="/usecase/large-loss-alerts">
                Use Case 1 · Large Loss Alerts
              </a>
              <a href="/usecase/early-severity-signal-detection">
                Use Case 2 · Early Severity Signal Detection
              </a>
              <a href="/usecase/reserve-adequacy-monitoring">
                Use Case 3 · Reserve Adequacy Monitoring (Incurred Below
                Prediction)
              </a>
              <a href="/usecase/high-severity-potential-claims">
                Use Case 4 · High Severity Potential Claims
              </a>
              <a href="/usecase/proactive-severity-litigation-intelligence">
                Use Case 5 · Proactive Severity & Litigation Intelligence
              </a>
              <a href="/usecase/intelligent-claims-document-summarization">
                Use Case 6 · Intelligent Claims Document Summarization
              </a>
            </div>

            <h1 style={{ fontWeight: "bold" }}>
              Surface Early Severity Signals <br />— Act Before Costs Escalate
            </h1>
            <p>
              Predicting the severity of insurance claims is essential for
              accurate risk assessment, effective triage, and timely reserves
              management. Yet with increasing claim complexity, staffing gaps,
              social inflation, and rising attorney involvement, severity
              determination for open claims has become a major challenge.
            </p>
            <br />
            <p className="esd-muted">
              Identifying and scoring potential high-severity claims early—while
              flagging low-severity claims or medium risks needing intervention
              before attorney involvement—is crucial to avoid downstream
              escalations and cost inflation. Charlee’s AI-driven severity
              scoring applies patterns learned across millions of claims to
              surface early warning signs—helping claims managers allocate the
              right resources, flag legal risk, and take proactive steps before
              litigation begins.
            </p>
          </section>

          <section className="esd-grid esd-cols-3">
            <div className="esd-card">
              <h3 style={{ fontWeight: "bold" }}>Role</h3>
              <p>Claims Manager, Supervisor, Adjuster/Handler</p>
            </div>
            <div className="esd-card">
              <h3 style={{ fontWeight: "bold" }}>Job at Hand</h3>
              <p>Proactively identify and manage potential legal exposure</p>
            </div>
            <div className="esd-card">
              <h3 style={{ fontWeight: "bold" }}>Trigger</h3>
              <p>
                Open claims approaching 30–60 days without attorney
                representation
              </p>
            </div>
          </section>

          <section className="esd-section">
            <div className="esd-headline">
              <h2 style={{ margin: 0, fontWeight: "bold" }}>Opportunity</h2>
              <div className="esd-rule" />
            </div>
            <div className="esd-card">
              <p>
                Getting ahead of attorney involvement enables timely
                investigation, informed outreach, and better control over loss
                costs.
              </p>
            </div>
          </section>

          <section className="esd-grid esd-cols-2 esd-section">
            <div className="esd-card">
              <h3 style={{ fontWeight: "bold" }}>Before Charlee</h3>
              <p>
                Aging claims were tracked manually, with no reliable way to
                detect early signs of litigation or attorney involvement. Key
                injury and treatment developments often crossed critical
                investigation windows unnoticed.
              </p>
            </div>
            <div className="esd-card">
              <h3 style={{ fontWeight: "bold" }}>With Charlee</h3>
              <p id="product">
                <strong>4SeeCharlee</strong> continuously monitors open claim
                inventory, using AI to detect and flag patterns that precede
                attorney engagement or litigation. It prioritizes claims for
                early action, helping teams intervene before legal complexity
                and costs escalate.
              </p>
            </div>
          </section>

          <section className="esd-section">
            <div className="esd-grid esd-cols-3">
              <div className="esd-card">
                <h3 style={{ fontWeight: "bold" }}>Product Used</h3>
                <div className="esd-badges">
                  <span className="esd-badge" title="4SeeCharlee">
                    🔎 4SeeCharlee
                  </span>
                </div>
              </div>
              <div className="esd-card">
                <h3 style={{ fontWeight: "bold" }}>LOBs</h3>
                <div className="esd-badges">
                  <span className="esd-badge">All</span>
                </div>
              </div>
              <div className="esd-card" id="value">
                <h3 style={{ fontWeight: "bold" }}>Area</h3>
                <div className="esd-badges">
                  <span className="esd-badge">Claims</span>
                  <span className="esd-badge">Severity</span>
                </div>
              </div>
            </div>
          </section>

          <section className="esd-section">
            <div className="esd-headline">
              <h2 style={{ margin: 0, fontWeight: "bold" }}>
                Value Proposition
              </h2>
              <div className="esd-rule" />
            </div>
            <div className="esd-card">
              <ul style={{ listStyleType: "disc", paddingLeft: "50px" }}>
                <li>
                  Early severity prediction enables risk-based claim
                  segmentation and appropriate departmental handling.
                </li>
                <li id="financial-impact">
                  Supports more targeted oversight and review processes.
                </li>
                <li>
                  Improves cycle times, boosts adjuster productivity, and helps
                  mitigate downstream litigation risk.
                </li>
              </ul>
            </div>
          </section>

          <section className="esd-section">
            <div className="esd-headline">
              <h2 style={{ margin: 0, fontWeight: "bold" }}>
                Financial Impact
              </h2>
              <div className="esd-rule" />
            </div>
            <div className="esd-card">
              <p className="esd-muted">
                Late recognition of high-severity claims often leads to:
              </p>
              <ul style={{ listStyleType: "disc", paddingLeft: "50px" }}>
                <li>
                  Unplanned attorney involvement, increasing average claim costs
                  by $15K–$40K
                </li>
                <li>
                  Missed negotiation opportunities, delaying closure and
                  inflating reserves
                </li>
                <li>
                  Higher Loss Adjustment Expenses (LAE) due to late-stage legal
                  interventions
                </li>
              </ul>
              <div className="esd-callout" style={{ marginTop: 12 }}>
                <strong>Charlee enables:</strong>
                <ul style={{ listStyleType: "disc", paddingLeft: "50px" }}>
                  <li>
                    $8K–$20K average savings per flagged claim via early
                    outreach and containment
                  </li>
                  <li>
                    Up to 25% reduction in attorney representation after 90 days
                  </li>
                  <li>
                    Improved loss ratios and better supervisor bandwidth
                    allocation
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className="esd-section">
            <div className="esd-headline">
              <h2 style={{ margin: 0, fontWeight: "bold" }}>Results</h2>
              <div className="esd-rule" />
            </div>
            <div className="esd-card">
              <div className="esd-metrics">
                <div
                  className="esd-metric"
                  aria-label="Proactive outreach result"
                >
                  <h4>🚀 40%</h4>
                  <p>
                    of flagged claims received proactive pre-litigation outreach
                  </p>
                </div>
                <div
                  className="esd-metric"
                  aria-label="Attorney reduction result"
                >
                  <h4>✅ 25%</h4>
                  <p>reduction in attorney representation after 90 days</p>
                </div>
                <div className="esd-metric" aria-label="Customer quote">
                  <h4>💬</h4>
                  <p>“We’re finally out in front of the litigation curve.”</p>
                </div>
              </div>
            </div>
          </section>

          <footer className="esd-footer">
            © {year} Charlee.ai — Use Case 2: Early Severity Signal Detection
          </footer>
        </main>
      </div>
    </div>
  );
};
